// ----------------------------------------------------------------------------
// AuthGateway
// ----------------------------------------------------------------------------
// Pre-auth splash. Two-tab sign-in / sign-up card with a 1.5s simulated
// session-establishment animation, then promotes the user into the dashboard.
//
// PRODUCTION LAUNCH NOTE — replace handleSubmit() with:
//
//   const supabase = createClient(ownerSettings.supabaseUrl,
//                                 ownerSettings.supabaseKey);
//
//   // Sign-In:
//   const { data, error } =
//     await supabase.auth.signInWithPassword({ email, password });
//
//   // Sign-Up:
//   const { data, error } =
//     await supabase.auth.signUp({ email, password,
//        options: { data: { company } } });
//
//   if (data?.session) signIn({ email, company });
//
// RLS policies provisioned by the Owner Setup Wizard automatically scope
// every subsequent DB read/write to the signed-in tenant.
// ----------------------------------------------------------------------------

import React, { useEffect, useRef, useState } from "react";
import {
  Compass,
  Building2,
  ArrowRight,
  Loader2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { AUTH } from "../constants/testIds";
import { useAuth } from "../context/AuthContext";

const STATUS_STAGES = [
  "Establishing secure user session…",
  "Verifying tenant database records…",
];

export const AuthGateway = () => {
  const { signIn } = useAuth();
  const [tab, setTab] = useState("signin"); // 'signin' | 'signup'
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const timers = useRef([]);

  useEffect(() => {
    return () => timers.current.forEach(clearTimeout);
  }, []);

  // --------------------------------------------------------------------------
  // handleSubmit — front-door auth handler.
  // Visual simulation (1.5s) → flips global isAuthenticated to true.
  // For the demo/tester flow this is a click-through gateway: no credentials
  // are validated client-side. SWAP THIS for a real Supabase / Firebase /
  // Cognito call — see top-of-file note.
  // --------------------------------------------------------------------------
  const handleSubmit = (e) => {
    e?.preventDefault();
    setLoading(true);
    setStage(0);
    timers.current.forEach(clearTimeout);
    timers.current = [
      setTimeout(() => setStage(1), 750),
      setTimeout(() => {
        signIn({
          email: company ? `demo@${company.toLowerCase().replace(/\s+/g, "")}.com` : "demo@spacecoast.test",
          company: company || "Demo Vendor",
          isNew: tab === "signup",
        });
        toast.success("Welcome to the Space Coast Procurement Portal");
      }, 1500),
    ];
  };

  return (
    <div
      data-testid={AUTH.gateway}
      className="min-h-screen w-full bg-[#0b1220] text-slate-100 relative overflow-hidden flex items-center justify-center px-4 py-10"
    >
      {/* Decorative slate-blue background flourishes */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-sky-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-32 h-[460px] w-[460px] rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.07]"
             style={{
               backgroundImage:
                 "radial-gradient(circle at 1px 1px, #94a3b8 1px, transparent 0)",
               backgroundSize: "32px 32px",
             }} />
      </div>

      {/* Card */}
      <div className="relative w-full max-w-md bg-white text-slate-900 rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Brand strip */}
        <div className="px-7 pt-7 pb-5 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-11 w-11 rounded-md bg-[#0f172a] border border-slate-800 flex items-center justify-center">
              <Compass size={22} strokeWidth={1.6} className="text-sky-400" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                Vendor Workspace
              </p>
              <h1 className="font-display text-lg font-semibold text-slate-900 leading-tight">
                Space Coast Procurement Portal
              </h1>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="relative grid grid-cols-2 rounded-lg bg-slate-100 p-1">
            <button
              type="button"
              data-testid={AUTH.tabSignIn}
              onClick={() => setTab("signin")}
              disabled={loading}
              className={`relative z-10 h-9 rounded-md text-xs font-semibold transition-colors ${
                tab === "signin" ? "text-slate-900" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              data-testid={AUTH.tabSignUp}
              onClick={() => setTab("signup")}
              disabled={loading}
              className={`relative z-10 h-9 rounded-md text-xs font-semibold transition-colors ${
                tab === "signup" ? "text-slate-900" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Create Account (14-Day Free Trial)
            </button>
            <span
              aria-hidden="true"
              className="absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-md shadow-sm border border-slate-200 transition-transform duration-200 ease-out"
              style={{
                transform: tab === "signin" ? "translateX(4px)" : "translateX(calc(100% + 4px))",
              }}
            />
          </div>
        </div>

        {/* Form */}
        <form className="px-7 py-7 space-y-5" onSubmit={handleSubmit}>
          {tab === "signup" && (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 mb-1.5">
                Company Name <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span>
              </label>
              <div className="relative">
                <Building2 size={15} strokeWidth={1.7} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid={AUTH.inputCompany}
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g., Space Coast Commercial Services"
                  className="pl-9 h-11 bg-white border-slate-200"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          <div className="rounded-md border border-sky-100 bg-sky-50/60 px-4 py-3 flex items-start gap-2.5">
            <Sparkles size={14} strokeWidth={1.7} className="text-sky-600 mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-sky-900/85 leading-relaxed">
              <span className="font-semibold">Tester preview mode.</span> Click below to
              enter the dashboard instantly — no credentials required.
            </p>
          </div>

          <div className="flex justify-center pt-1">
            <button
              type="submit"
              data-testid={AUTH.submit}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 h-12 px-10 rounded-md bg-[#0c63e7] hover:bg-[#0a4eb8] text-white font-semibold text-sm shadow-sm transition-colors disabled:opacity-90 disabled:cursor-not-allowed min-w-[220px]"
            >
              {loading ? (
                <>
                  <Loader2 size={16} strokeWidth={2} className="animate-spin" />
                  {tab === "signin" ? "Signing in…" : "Provisioning account…"}
                </>
              ) : (
                <>
                  Access Dashboard
                  <ArrowRight size={15} strokeWidth={2} />
                </>
              )}
            </button>
          </div>

          {loading && (
            <div
              data-testid={AUTH.status}
              className="rounded-md border border-indigo-100 bg-indigo-50/60 p-3 flex items-center gap-2.5 animate-fade-in"
            >
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-500 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-indigo-600" />
              </span>
              <p className="text-xs text-indigo-900 font-medium">
                {STATUS_STAGES[stage]}
              </p>
            </div>
          )}

          {/* Trust footer */}
          <div className="pt-2 grid grid-cols-2 gap-2 text-[11px]">
            <div className="flex items-center gap-1.5 text-slate-500">
              <ShieldCheck size={12} strokeWidth={1.7} className="text-emerald-600" />
              SOC 2 · 256-bit encrypted
            </div>
            <div className="flex items-center gap-1.5 text-slate-500 justify-end">
              <Sparkles size={12} strokeWidth={1.7} className="text-amber-500" />
              No credit card required
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
